import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import toast from "../../utils/toast";
import tubeTypeService from "../../services/tubeTypeService";

export const fetchTubeTypes = createAsyncThunk(
  "tubeTypes/fetchTubeTypes",
  async (activeOnly = true, { rejectWithValue }) => {
    try {
      const response = await tubeTypeService.getAllTubeTypes(activeOnly);
      return response.data.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || "Failed to fetch tube types";
      toast.error(errorMsg);
      return rejectWithValue(errorMsg);
    }
  }
);

export const createTubeType = createAsyncThunk(
  "tubeTypes/createTubeType",
  async (data, { rejectWithValue }) => {
    try {
      const response = await tubeTypeService.createTubeType(data);
      toast.success(response.data.message || "Tube type created successfully");
      return response.data.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || "Failed to create tube type";
      const validationErrors = error.response?.data?.errors;
      if (validationErrors) {
        Object.values(validationErrors).forEach((err) => {
          toast.error(Array.isArray(err) ? err[0] : err);
        });
      } else {
        toast.error(errorMsg);
      }
      return rejectWithValue(errorMsg);
    }
  }
);

export const updateTubeType = createAsyncThunk(
  "tubeTypes/updateTubeType",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await tubeTypeService.updateTubeType(id, data);
      toast.success(response.data.message || "Tube type updated successfully");
      return response.data.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || "Failed to update tube type";
      toast.error(errorMsg);
      return rejectWithValue(errorMsg);
    }
  }
);

export const deleteTubeType = createAsyncThunk(
  "tubeTypes/deleteTubeType",
  async (id, { rejectWithValue }) => {
    try {
      const response = await tubeTypeService.deleteTubeType(id);
      toast.success(response.data.message || "Tube type deleted successfully");
      return id;
    } catch (error) {
      const errorMsg = error.response?.data?.message || "Failed to delete tube type";
      toast.error(errorMsg);
      return rejectWithValue(errorMsg);
    }
  }
);

const tubeTypeSlice = createSlice({
  name: "tubeTypes",
  initialState: {
    items: [],
    loading: false,
    error: null,
  },
  reducers: {
    clearTubeTypeError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTubeTypes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTubeTypes.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchTubeTypes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createTubeType.fulfilled, (state, action) => {
        state.items.push(action.payload);
      })
      .addCase(updateTubeType.fulfilled, (state, action) => {
        const updated = action.payload;
        const idx = state.items.findIndex((t) => t.id === updated.id);
        if (idx !== -1) state.items[idx] = updated;
      })
      .addCase(deleteTubeType.fulfilled, (state, action) => {
        state.items = state.items.filter((t) => t.id !== action.payload);
      });
  },
});

export const { clearTubeTypeError } = tubeTypeSlice.actions;
export default tubeTypeSlice.reducer;
